import { Bell, ChevronDown, ClipboardList, Globe, LogOut, Printer, ShoppingBag, ShoppingCart, Store, Building2, MapPin, Package, Layers } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signOut, useAuth } from '@/hooks/useAuth';
import { useChannels, type Channel } from '@/hooks/useChannels';
import { useActiveChannel } from '@/store/activeChannel';
import { useNotifications } from '@/hooks/useNotifications';
import { GlobalSearch } from '@/components/layout/GlobalSearch';
import { dropdownVariants } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/format';

// ─── Ícone por tipo de canal ──────────────────────────────────────────────────

const KIND_ICONS: Record<Channel['kind'], React.ComponentType<{ className?: string }>> = {
  mercado_livre: ShoppingBag,
  shopee:        ShoppingCart,
  shopify:       Store,
  site_proprio:  Globe,
  atacado:       Building2,
  loja_fisica:   MapPin,
  amazon:        Package,
  outro:         Layers,
};

// ─── Channel Switcher ─────────────────────────────────────────────────────────

function ChannelSwitcher() {
  const { channels, loading, defaultChannel } = useChannels();
  const { activeChannelId, setActiveChannel } = useActiveChannel();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // BPO channel switcher is irrelevant on Farms routes
  if (location.pathname.startsWith('/farms')) return null;

  const active = activeChannelId
    ? (channels.find((c) => c.id === activeChannelId) ?? defaultChannel)
    : null;

  const displayName = active ? active.name : 'Geral';
  const ActiveIcon = active ? KIND_ICONS[active.kind] : Layers;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="btn-secondary !py-1.5 !px-3 text-left gap-2"
        disabled={loading}
      >
        <ActiveIcon className="w-4 h-4 text-brand-700 shrink-0" />
        <span className="text-ink font-semibold truncate max-w-[140px]">{displayName}</span>
        <ChevronDown className="w-4 h-4 text-ink-muted shrink-0" />
      </button>

      <AnimatePresence>
      {open && (
        <motion.div
          variants={dropdownVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          className="absolute mt-2 w-64 card p-2 z-30 shadow-xl origin-top-left"
        >
          <p className="px-3 py-1.5 stat-label">Canal ativo</p>

          {/* Opção "Geral" — todos os canais */}
          <button
            onClick={() => { setActiveChannel(null); setOpen(false); }}
            className={cn(
              'w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors',
              !activeChannelId ? 'bg-brand-50 text-brand-800 font-semibold' : 'hover:bg-surface-subtle text-ink'
            )}
          >
            <Layers className="w-4 h-4 shrink-0" />
            Geral (todos os canais)
          </button>

          {channels.length > 0 && <div className="my-1 border-t border-border" />}

          {channels.map((c) => {
            const Icon = KIND_ICONS[c.kind];
            const isActive = activeChannelId === c.id;
            return (
              <button
                key={c.id}
                onClick={() => { setActiveChannel(c.id); setOpen(false); }}
                className={cn(
                  'w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors',
                  isActive ? 'bg-brand-50 text-brand-800 font-semibold' : 'hover:bg-surface-subtle text-ink'
                )}
              >
                <span style={{ color: c.color }} className="shrink-0 flex"><Icon className="w-4 h-4" /></span>
                {c.name}
              </button>
            );
          })}

          <div className="my-1 border-t border-border" />
          <button
            onClick={() => { setOpen(false); navigate('/configuracoes/canais'); }}
            className="w-full text-left px-3 py-2 rounded-lg text-xs text-ink-muted hover:bg-surface-subtle transition-colors"
          >
            + Gerenciar canais
          </button>
        </motion.div>
      )}
      </AnimatePresence>
    </div>
  );
}

// ─── Ícone por tipo de notificação ───────────────────────────────────────────

const NOTIF_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  new_order:    Printer,
  order_status: ClipboardList,
  new_message:  ShoppingBag,
};

// ─── Notificações ─────────────────────────────────────────────────────────────

function NotificationBell() {
  const [open, setOpen]                             = useState(false);
  const ref                                         = useRef<HTMLDivElement>(null);
  const navigate                                    = useNavigate();
  const { items, unreadCount, markRead, markAllRead } = useNotifications();

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  function handleOpen() {
    setOpen((v) => !v);
  }

  async function handleClick(id: string, link: string | null, read: boolean) {
    if (!read) await markRead(id);
    setOpen(false);
    if (link) navigate(link);
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={handleOpen}
        className="btn-ghost !p-2 relative"
        aria-label="Notificações"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-[#14a655] text-white text-[10px] font-bold flex items-center justify-center leading-none">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
      {open && (
        <motion.div
          variants={dropdownVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          className="absolute right-0 mt-2 w-80 card z-30 shadow-xl overflow-hidden origin-top-right"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <p className="text-xs font-semibold text-ink-muted uppercase tracking-wider">Notificações</p>
            {unreadCount > 0 && (
              <button onClick={() => void markAllRead()} className="text-xs text-brand-700 hover:underline">
                Marcar todas como lidas
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-8 text-center px-4">
              <Bell className="w-8 h-8 text-ink-soft" />
              <p className="text-sm text-ink-muted">Nenhuma notificação ainda.</p>
              <p className="text-xs text-ink-soft">Alertas de pedidos e atualizações aparecerão aqui.</p>
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto divide-y divide-border">
              {items.map((n) => {
                const Icon = NOTIF_ICONS[n.type] ?? Bell;
                return (
                  <button
                    key={n.id}
                    onClick={() => void handleClick(n.id, n.link, n.read)}
                    className={cn(
                      'w-full text-left px-4 py-3 flex items-start gap-3 transition-colors hover:bg-surface-subtle',
                      !n.read && 'bg-[#14a655]/5'
                    )}
                  >
                    <div className={cn(
                      'w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5',
                      !n.read ? 'bg-[#14a655]/15' : 'bg-surface-subtle'
                    )}>
                      <Icon className={cn('w-4 h-4', !n.read ? 'text-[#14a655]' : 'text-ink-muted')} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={cn('text-sm leading-snug', !n.read ? 'font-semibold text-ink' : 'text-ink-muted')}>
                        {n.title}
                      </p>
                      {n.body && <p className="text-xs text-ink-muted mt-0.5 line-clamp-1">{n.body}</p>}
                      <p className="text-[11px] text-ink-soft mt-1">{formatDate(n.created_at)}</p>
                    </div>
                    {!n.read && (
                      <div className="w-2 h-2 rounded-full bg-[#14a655] shrink-0 mt-2" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </motion.div>
      )}
      </AnimatePresence>
    </div>
  );
}

// ─── Header principal ─────────────────────────────────────────────────────────

export function Header() {
  const { user } = useAuth();

  return (
    <header className="h-16 shrink-0 flex items-center justify-between gap-4 px-4 lg:px-8 border-b border-border bg-surface-raised">
      {/* Channel switcher */}
      <ChannelSwitcher />

      {/* Search global com autocomplete */}
      <div className="hidden md:flex flex-1 max-w-md mx-auto">
        <GlobalSearch />
      </div>

      {/* MYND Farms button */}
      <Link to="/farms" className="btn-secondary !py-1.5 !px-3 gap-2 hidden md:flex shrink-0" style={{ borderColor: '#14a655', color: '#14a655' }}>
        <Printer className="w-4 h-4" />
        <span className="font-semibold text-sm">MYND Farms</span>
      </Link>

      {/* Right cluster */}
      <div className="flex items-center gap-2">
        <NotificationBell />

        <div className="flex items-center gap-2 pl-2 ml-2 border-l border-border">
          <div className="w-8 h-8 rounded-full bg-brand-700 text-ink-inverse flex items-center justify-center font-semibold text-sm">
            {(user?.email ?? '?').charAt(0).toUpperCase()}
          </div>
          <div className="hidden md:block">
            <p className="text-sm font-medium text-ink leading-tight">{user?.email ?? 'Convidado'}</p>
            <p className="text-xs text-ink-muted leading-tight">Admin</p>
          </div>
          <button
            onClick={() => void signOut()}
            className="btn-ghost !p-2 ml-1"
            aria-label="Sair"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </header>
  );
}
